import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { replace as Replace } from 'redux-first-history';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { parse } from 'query-string';
import { isEmpty } from 'lodash-es';
import {
    CustomTable,
    CustomTableHeadersProps,
    Pagination,
    palette,
    MobileTable,
    MobileCardRowsProps,
    EmptyState,
} from '@dev/ee-components';
import { DeliveryLogType } from '@dev/ee-api/ee-api-models';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

import useMobile from '@/services/hooks/useMobile';
import { getReportsState } from '@/store/reports/actions';
import { SetEmailMsgid } from '@/store/emails/actions';
import { EventLogByChannelNameStart, ClearCampaignsReportsData } from '@/store/reports/actions';
import { EventLogParamsByChannelName } from '@/store/reports/types';
import { LONG_DATE_FORMAT } from '@/services/constants';
import { addParamToUrl } from '@/services/tools/handleUrlParams';
import SearchAndRefresh from './SearchAndRefresh';
import { useTableRows } from './hooks/useTableRows';
import { useTableHeaders } from './hooks/useTableHeaders';

interface TableContainerProps {
    isMobile?: boolean;
}
const TableContainer = styled.div<TableContainerProps>`
    border-bottom: 1px solid ${palette.gray[3]};
    ${(props) => (props.isMobile ? 'margin: 0 20px' : '')};
`;

const LogContainer = styled.div`
    border: 1px solid ${palette.gray[3]};
    border-radius: 4px;
    margin-top: 24px;
    padding: 32px 0 0;
    background-color: #fff;
`;

const PaginationWrapper = styled.div`
    .numberOfItems {
        text-align: center;
    }
`;

const MobileActivityTable = (): JSX.Element => {
    const { eventLogByChannelName } = useSelector(getReportsState);
    const { t } = useTranslation();
    const headers = [t('to'), t('event'), t('date'), t('result')];
    const rows = useTableRows();
    const isMobile = useMobile();
    const dispatch = useDispatch();

    if (isEmpty(eventLogByChannelName)) {
        return <EmptyState text={t('thereisnodatatodisplay')} />;
    }

    return (
        <TableContainer isMobile={isMobile}>
            <MobileTable
                headers={headers}
                rows={rows as MobileCardRowsProps[]}
                onClickButton={(e, rows) => {
                    dispatch(SetEmailMsgid(rows.returnOnItemClick));
                }}
                expandedRows={{ to: true, logsDate: true } as any}
            />
        </TableContainer>
    );
};

const Footer = (): JSX.Element => {
    const { eventLogByChannelNameLoading, eventsByChannelNameCount, eventLogParamsByChannelName } =
        useSelector(getReportsState);

    const totalCount = eventLogByChannelNameLoading ? 0 : eventsByChannelNameCount;
    const activePage =
        ((eventLogParamsByChannelName?.offset || 0) + (eventLogParamsByChannelName?.limit || 0)) /
        (eventLogParamsByChannelName?.limit || 1);
    const { name } = useParams<{ name: string }>();
    const dispatch = useDispatch();
    const isMobile = useMobile();

    const changePage = (page: number, perPage: number): void => {
        const params: EventLogParamsByChannelName = {
            statuses: eventLogParamsByChannelName?.statuses,
            offset: page * perPage - perPage,
            limit: perPage,
            channelName: safelyDecodeBase64Url(name),
            messageCategory: eventLogParamsByChannelName?.messageCategory,
            email: eventLogParamsByChannelName?.email,
            lastEvent: true,
            from: eventLogParamsByChannelName.from,
            to: eventLogParamsByChannelName.to,
        };

        const newSearch = addParamToUrl({ perPage, page });
        dispatch(Replace({ search: newSearch }));
        dispatch(EventLogByChannelNameStart(params));
    };

    return isMobile ? (
        <PaginationWrapper>
            <Pagination
                dataLength={totalCount}
                perPage={eventLogParamsByChannelName?.limit}
                activePage={activePage}
                handlePaginationChange={changePage}
            />
        </PaginationWrapper>
    ) : (
        <Pagination
            dataLength={totalCount}
            perPage={eventLogParamsByChannelName?.limit}
            activePage={activePage}
            handlePaginationChange={changePage}
        />
    );
};

const ActivityTable = (): JSX.Element => {
    const { eventLogByChannelNameLoading, eventLogByChannelName } = useSelector(getReportsState);
    const { t } = useTranslation();
    const headers = useTableHeaders();

    const columnWidth: Record<string, string> = {
        to: '40%',
        logsDate: '20%',
        emptyColumn: '5%',
        event: '15%',
        result: '15%',
        icon: '5%',
    };

    return (
        <TableContainer>
            <CustomTable
                headers={headers as CustomTableHeadersProps[]}
                rows={useTableRows()}
                isEmpty={isEmpty(eventLogByChannelName)}
                loading={eventLogByChannelNameLoading}
                stickyHeader={false}
                rowPadding={48}
                rowHeight={60}
                hideCheckboxes={true}
                columnsWidth={columnWidth}
                emptyStateProps={{ text: t('thereisnodatatodisplay') }}
                disableRowClick={true}
                disableFirstRowBorderTop={true}
            />
        </TableContainer>
    );
};

export const InProgressActivity = (): JSX.Element => {
    const { eventLogParamsByChannelName } = useSelector(getReportsState);
    const { name } = useParams<{ name: string }>();
    const decodeName = safelyDecodeBase64Url(name);
    const dispatch = useDispatch();
    const location = useLocation();
    const isMobile = useMobile();

    const query = parse(location.search);
    const params: EventLogParamsByChannelName = {
        statuses: query?.statuses
            ? ([...query.statuses] as DeliveryLogType[])
            : [DeliveryLogType.ReadyToSend, DeliveryLogType.WaitingToRetry, DeliveryLogType.Sending],
        channelName: decodeName,
        email: query?.search as string,
        messageCategory: eventLogParamsByChannelName?.messageCategory,
        offset:
            (parseInt(query?.page as string, 10) - 1) * parseInt(query?.perPage as string, 10) ||
            eventLogParamsByChannelName?.offset,
        limit:
            (parseInt(query?.page as string, 10) - 1) * parseInt(query?.perPage as string, 10) ||
            eventLogParamsByChannelName?.limit,
        lastEvent: true,
        from: dayjs(dayjs().format(LONG_DATE_FORMAT)).subtract(2, 'day').format(LONG_DATE_FORMAT),
        to: dayjs().format(LONG_DATE_FORMAT),
    };

    useEffect(() => {
        dispatch(EventLogByChannelNameStart(params));

        return () => {
            dispatch(ClearCampaignsReportsData());
        };
    }, []);

    return (
        <LogContainer>
            <SearchAndRefresh />
            {isMobile ? <MobileActivityTable /> : <ActivityTable />}
            <Footer />
        </LogContainer>
    );
};

export default InProgressActivity;
